import PropTypes from 'prop-types';
import React from 'react';
import { APPLICATION_PATHS } from '../application-core/constants';
import Errored from '../core/Errored';
import ExternalResourceLink from '../core/ExternalResourceLink';
import Loading from '../core/Loading';
import Paging from '../core/Paging';
import ResourceLink from '../core/ResourceLink';
import TitledPanel from '../core/TitledPanel';
import { isInOntology, makeCancelable, sortObjects } from '../core/utils/utils';
import { MAX_EXCLUDE_RESOURCES } from '../core/utils/constants';
import GS from '../grafsync/grafsync';
import FindResourceDialog from './FindResourceDialog';

const PAGE_SIZE = 500;
const MAX_NODES = 5000;

class ResourceRelationshipsDisplay extends React.Component {

  constructor(props) {
    super(props);
    this.state = {
      page: 1,
      isLoading: props.values ? props.values.length > 0 : false,
      initialLoading: props.values ? props.values.length > 0 : false,
      totalRelations: props.values ? props.values.length : 0,
      resources: undefined,
      viewLang: undefined,
      isErrored: undefined
    };
    this.cancellable = null;
  }

  static getDerivedStateFromProps(props, state) {

    if (!state.isLoading && props.values && (props.values.length !== state.totalRelations || props.viewLang !== state.viewLang)) {
      // If a deletion happens that reduces page count and we're on the last page we reset page to page count
      const pageCount = Math.floor((props.values.length + PAGE_SIZE - 1) / PAGE_SIZE);
      let page = state.page;
      if (page > pageCount && pageCount > 0) page = pageCount;
      else if (page > pageCount && pageCount === 0) page = 1;

      return props.values.length === 0 ?
        {
          resources: [],
          isLoading: false,
          page,
          viewLang: props.viewLang,
          totalRelations: 0
        }
        :
        {
          resources: undefined,
          isLoading: true,
          page,
          viewLang: props.viewLang,
          totalRelations: props.values.length
        };
    }
    return null;
  }

  componentDidUpdate() {
    if (!this.state.isErrored && this.state.resources === undefined && this.props.values && this.props.values.length > 0) {
      this.collectResourceData();
    }
  }

  componentDidMount() {
    if (this.props.values && this.props.values.length > 0) {
      this.collectResourceData();
    }
    else {
      this.setState({ isLoading: false });
    }
  }

  loadData(iris) {
    this.cancellable = makeCancelable(GS.loadData(iris, {}, this.props.graph));
    return this.cancellable;
  }

  componentWillUnmount() {
    if (this.cancellable) this.cancellable.cancel();
  }

  async collectResourceData() {
    // We load ALL the resources necessary at this level in the tree in one API call to avoid unnecessary calls
    // There may be more than 5000 children so we need to load in pages just in case!
    try {
      GS.handle("collect-resources");
      const pages = Math.floor((this.props.values.length + MAX_NODES - 1) / MAX_NODES);
      for (let page = 0; page < pages; page++) {
        const pageStart = page * MAX_NODES;
        const pageEnd = (page + 1) * MAX_NODES;
        const pageChildren = this.props.values.slice(pageStart, pageEnd);
        await this.loadData(pageChildren).promise;
        if (this.cancellable && this.cancellable.isCancelled()) return;
      }
      // Once all loaded then we sort
      this.processResources();
      if (this.cancellable && !this.cancellable.isCancelled()) {
        this.setState({ isLoading: false, initialLoading: false });
      }
    }
    catch (e) {
      if (!this.cancellable || (this.cancellable && !this.cancellable.isCancelled())) this.setState({ isErrored: e });
    }
  }

  processResources() {
    let resources = this.props.values.map(id => {
      // Things like XSD namespace won't necessarily be found
      if (!GS.isLoaded(id, this.props.graph)) {
        return { id, notFound: true };
      }
      const data = GS.data(id, this.props.graph);
      if (!data.type) return { id, notFound: true };
      const dataTypeList = Array.isArray(data.type) ? data.type : [data.type];
      if (this.props.permittedExternalType && !dataTypeList.includes(this.props.permittedExternalType)) {
        return { id, notFound: true, invalidType: true };
      }
      return data;
    });
    sortObjects(resources, 'label', this.props.viewLang, this.props.defaultLang, false);
    if (this.cancellable && !this.cancellable.isCancelled()) this.setState({ resources });
  }

  monitorLinkUpdate = (iri, property) => {
    if (property === "label") this.processResources();
  };

  handlePageChange = async (page = 1) => this.setState({ page });

  render() {

    const { view, title, isReadOnly, defaultLang, source, project, graph, includeSystemGraph,
      viewLang, highlight, permitExternal, searchExcludeClass, values,
      excludeByPath, excludeByPathList, property, addNewResource, buildLink, ontologyIRI, searchClass, dataTest, components } = this.props;
    const { resources, page, isErrored, initialLoading } = this.state;

    if (isErrored) return <Errored dataTest="rr-error" data={isErrored} />;

    const excludedResources = resources ? resources.map(resource => resource.id) : [];
    const exclusions = this.props.exclude ?
      this.props.exclude.filter(exc => !excludedResources.includes(exc))
      :
      [];
    const pageCount = resources ? Math.floor((resources.length + PAGE_SIZE - 1) / PAGE_SIZE) : 0;
    const startNode = (page - 1) * PAGE_SIZE;

    return (
      <TitledPanel
        dataTest={dataTest}
        height="full"
        title={title + (resources && resources.length > 500 ? ` (${resources.length})` : '')}
        highlight={highlight}
        actions=
        {!isReadOnly && !initialLoading && (!resources || resources.length < MAX_EXCLUDE_RESOURCES) ?
          <FindResourceDialog
            key={viewLang}
            source={source}
            permitExternal={permitExternal}
            components={components}
            defaultLang={defaultLang}
            ontologyIRI={ontologyIRI}
            searchExcludeClass={searchExcludeClass}
            searchClass={searchClass}
            excludeByPath={excludeByPath}
            excludeByPathList={excludeByPathList}
            includeSystemGraph={includeSystemGraph}
            title={title}
            project={project}
            graph={graph}
            viewLang={viewLang}
            exclude={[...exclusions, ...excludedResources]}
            onAddResource={addNewResource} />
          :
          null
        }>
        {pageCount > 1 ?
          <div>
            <Paging showText={false} page={page} pageCount={pageCount} handlePageChange={this.handlePageChange} />
          </div>
          :
          null
        }
        {initialLoading ? <Loading message={values.length > 1000 ? "There are a lot of resources to load!" : ""} /> : null}
        {resources && resources.slice(startNode, startNode + PAGE_SIZE).map(resource => {
          if (resource.notFound) {
            return <ExternalResourceLink
              dataTest={dataTest + "-" + resource.id}
              key={"rel-" + source + "-" + resource.id}
              id={resource.id}
              graph={graph}
              invalid={resource.invalidType}
              isReadOnly={isReadOnly}
              source={source}
              property={property} />;
          }
          else return <ResourceLink
            dataTest={dataTest + "-" + resource.id}
            key={"rel-" + source + "-" + resource.id}
            id={resource.id}
            graph={graph}
            projectGraph={graph}
            link={(data) => {
              if (isInOntology(data.id, data.isDefinedBy, ontologyIRI)) {
                return buildLink(view, resource.id);
              }
              else {
                // Visibility first because isDefinedBy maybe be different to ontology
                const ontology = data.visibility || data.isDefinedBy;
                if (!ontology) return null;
                return APPLICATION_PATHS.ONTOLOGY_MANAGER + '/project/ontology#project=' + project + "&ontology="
                  + ontology + "&view=" + view + "&" + view + "=" + resource.id;
              }
            }}
            context={ontologyIRI}
            contextProperty="isDefinedBy"
            isReadOnly={isReadOnly}
            source={source}
            updateEvent={this.monitorLinkUpdate}
            property={property}
            viewLang={viewLang}
            defaultLang={defaultLang} />;
        })}
        {pageCount > 1 ?
          <div>
            <Paging showText={false} page={page} pageCount={pageCount} handlePageChange={this.handlePageChange} />
          </div>
          :
          null
        }
      </TitledPanel>
    );
  }
}

ResourceRelationshipsDisplay.defaultProps = {
  highlight: false,
  permitExternal: false,
};

ResourceRelationshipsDisplay.propTypes = {
  highlight: PropTypes.bool,
  dataTest: PropTypes.string,
  buildLink: PropTypes.func.isRequired,
  graph: PropTypes.string.isRequired,
  ontologyIRI: PropTypes.string.isRequired,
  defaultLang: PropTypes.string,
  viewLang: PropTypes.string,
  components: PropTypes.array,
  excludeByPath: PropTypes.array,
  excludeByPathList: PropTypes.array,
  values: PropTypes.array,
  exclude: PropTypes.array,
  isReadOnly: PropTypes.bool,
  source: PropTypes.string,
  permittedExternalType: PropTypes.string,
  permitExternal: PropTypes.bool,
  includeSystemGraph: PropTypes.bool,
  addNewResource: PropTypes.func.isRequired,
  searchExcludeClass: PropTypes.array,
  searchClass: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  view: PropTypes.string.isRequired,
  property: PropTypes.string.isRequired,
  project: PropTypes.string.isRequired
};

export default ResourceRelationshipsDisplay;